<script setup lang="ts">
import {computed} from 'vue'
import {useStore} from 'vuex'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'

import { Org, OrgMember } from '@/types'
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group";
import {Switch} from "@/components/ui/switch";
import Label from "@/components/ui/label/Label.vue";

const store = useStore()

const props = defineProps<{
  org: Org,
  member: OrgMember,
  open: boolean,
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

const close = () => emit('update:open', false)

const keySettings = computed(() => Array.isArray(props.member?.key_settings) ? props.member.key_settings : [])

// Reactive so the form picks up the latest member values each time it is reopened
// (the dialog is always mounted, so a plain const would freeze the defaults).
const formSchema = computed(() => toTypedSchema(z.object({
  fullname: z.string().min(3).max(255).default(props.member.display_name),
  email: z.string().email().default(props.member.email),
  role: z.string().default(props.member.role),
  key_skysat: z.boolean().default(keySettings.value[0]?.enabled ?? false),
  key_basemaps: z.boolean().default(keySettings.value[1]?.enabled ?? false),
  key_planetscope: z.boolean().default(keySettings.value[2]?.enabled ?? false),
})))

const onSubmit = async (values: any) => {
  const payload = {
    displayName: values.fullname,
    role: values.role,
    orgId: props.org.organization_id,
    orgMemberId: props.member.organization_member_id,
    keySettings: [
      {"name": "Skysat", "enabled": values.key_skysat},
      {"name": "Basemaps", "enabled": values.key_basemaps},
      {"name": "Planetscope", "enabled": values.key_planetscope}
    ]
  }

  await store.dispatch('org/updateMember', payload)

  toast({
    title: `${values.email} edited`,
  })

  close()
}
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="max-w-[350px]">
      <DialogHeader>
        <DialogTitle>Edit member</DialogTitle>
        <DialogDescription>Member details</DialogDescription>
      </DialogHeader>

      <Form :key="String(open)" class="space-y-3" :validation-schema="formSchema" @submit="onSubmit">
        <FormField v-slot="{ componentField }" name="fullname">
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="email">
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="role">
          <FormItem>
            <FormControl>
              <RadioGroup v-bind="componentField">
                <FormItem class="flex items-center space-y-0 gap-x-3">
                  <FormControl>
                    <RadioGroupItem value="org_admin" />
                  </FormControl>
                  <FormLabel class="font-normal">Administrator</FormLabel>
                </FormItem>
                <FormItem class="flex items-center space-y-0 gap-x-3">
                  <FormControl>
                    <RadioGroupItem value="org_member" />
                  </FormControl>
                  <FormLabel class="font-normal">Member</FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
          </FormItem>
          <FormMessage />
        </FormField>

        <div class="mt-5">
          <h4 class="font-bold text-base">Planet Access</h4>
        </div>

        <FormField v-slot="{ value, handleChange }" name="key_skysat">
          <FormItem>
            <FormControl>
              <div class="flex items-center">
                <Switch
                  :model-value="value"
                  @update:model-value="handleChange"
                />
                <Label class="ml-2">Skysat</Label>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ value, handleChange }" name="key_basemaps">
          <FormItem>
            <FormControl>
              <div class="flex items-center">
                <Switch
                  :model-value="value"
                  @update:model-value="handleChange"
                />
                <Label class="ml-2">Basemaps</Label>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ value, handleChange }" name="key_planetscope">
          <FormItem>
            <FormControl>
              <div class="flex items-center">
                <Switch
                  :model-value="value"
                  @update:model-value="handleChange"
                />
                <Label class="ml-2">PlanetScope</Label>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <DialogFooter>
          <Button variant="ghost" type="button" @click="close">Close</Button>
          <Button type="submit">Edit</Button>
        </DialogFooter>
      </Form>
    </DialogContent>
  </Dialog>
</template>
